// Complete sitemap / table of contents — every page in chronological build order.
import { Link } from "react-router-dom";
import {
  Map, Home, LogIn, UserPlus, LayoutDashboard, UserCircle2, Users, Network as NetIcon,
  Building2, BookOpen, Library, Scale, Languages, Palette, Music4, FileText, Crown,
  CheckCircle2, ShieldCheck, AlertTriangle, RotateCcw, ExternalLink,
} from "lucide-react";
import { RIGHTS_TOPICS, TOPIC_SLUGS } from "../data/rightsTopics";
import { BRANDING_NATIONAL, BRANDING_INTERNATIONAL, BRANDING_ANTHROPOLOGY } from "../data/brandingAgencies";
import { ORCHESTRATION_RESOURCES } from "../data/orchestrationResources";
import { COUNTRY_AGENCIES } from "../data/countryAgencies";

const CATEGORY_SLUGS = [
  { slug: "music-agencies", label: "Music Agencies" },
  { slug: "publishers", label: "Publishers" },
  { slug: "distribution", label: "Distribution" },
  { slug: "masters", label: "Master Rights" },
  { slug: "domains", label: "Domain & ICANN" },
];

// Each section has chronological build order in mind: how the product came to be.
const SECTIONS = [
  {
    n: "01",
    title: "Entry & Public Home",
    desc: "First touchpoints any visitor sees, before they have an account.",
    icon: Home,
    items: [
      { path: "/", label: "Landing — Hero, bass-clef ship, registry overview", icon: Home },
      { path: "/sitemap", label: "Site Map — this page", icon: Map },
    ],
  },
  {
    n: "02",
    title: "Authentication",
    desc: "Account creation and member sign-in (JWT cookie auth).",
    icon: LogIn,
    items: [
      { path: "/register", label: "Create your account", icon: UserPlus },
      { path: "/login", label: "Member sign in", icon: LogIn },
    ],
  },
  {
    n: "03",
    title: "Member Workspace (login required)",
    desc: "Your private OSRR workspace once signed in.",
    icon: LayoutDashboard,
    items: [
      { path: "/dashboard", label: "Dashboard — saved agencies, recent activity", icon: LayoutDashboard, protected: true },
      { path: "/profile", label: "Profile — your composer/songwriter details", icon: UserCircle2, protected: true },
      { path: "/network", label: "Network — discover other members", icon: NetIcon, protected: true },
      { path: "/connections", label: "Connections — your accepted connections", icon: Users, protected: true },
    ],
  },
  {
    n: "04",
    title: "Agency Directory (200+ agencies, 160 countries)",
    desc: "The core OSRR registry. Browse by category or by country flag.",
    icon: Building2,
    items: [
      { path: "/agencies", label: "All Agencies — searchable master directory", icon: Building2, protected: true },
      ...CATEGORY_SLUGS.map((c) => ({
        path: `/category/${c.slug}`,
        label: `Category · ${c.label}`,
        icon: Building2,
      })),
    ],
  },
  {
    n: "05",
    title: "Rights Hubs (Master · Digital · Recording)",
    desc: "Three top-level rights categories with featured organizations.",
    icon: Library,
    items: [
      { path: "/rights/master", label: "Master Rights Hub — ownership, licensing, sync", icon: Library, protected: true },
      { path: "/rights/digital", label: "Digital Rights Hub — streaming, DSPs, downloads", icon: Library, protected: true },
      { path: "/rights/recording", label: "Recording Rights Hub — performers, neighbouring", icon: Library, protected: true },
    ],
  },
  {
    n: "06",
    title: "Rights Library & Topics (22 deep-linked topics)",
    desc: "Encyclopedic per-topic pages with official-government and private entities clearly separated.",
    icon: BookOpen,
    items: [
      { path: "/rights-library", label: "Rights Library — index of all topics", icon: Library },
      ...TOPIC_SLUGS.map((slug) => ({
        path: `/rights-topic/${slug}`,
        label: `Topic · ${RIGHTS_TOPICS[slug].title}`,
        icon: BookOpen,
      })),
    ],
  },
  {
    n: "07",
    title: "Copyright Laws & Country Agencies",
    desc: "U.S. and global copyright frameworks; clickable flags for 160+ country-specific rights agencies.",
    icon: Scale,
    items: [
      { path: "/copyright-laws", label: "Copyright Laws — gov vs. private separation", icon: Scale },
      ...COUNTRY_AGENCIES.slice(0, 20).map((c) => ({
        externalUrl: c.url,
        label: `${c.country} — ${c.agency}`,
        icon: ExternalLink,
        external: true,
      })),
      { note: `…and ${COUNTRY_AGENCIES.length - 20} more country agencies listed on the Copyright Laws page.` },
    ],
  },
  {
    n: "08",
    title: "Branding Hub (3 tiers)",
    desc: "National, International, and Anthropology branding firms relevant to music identity.",
    icon: Palette,
    items: [
      { path: "/branding", label: "Branding Hub — 3-tier overview", icon: Palette },
      { path: "/branding/national", label: `National Branding · ${BRANDING_NATIONAL.length} firms`, icon: Palette },
      { path: "/branding/international", label: `International Branding · ${BRANDING_INTERNATIONAL.length} firms`, icon: Palette },
      { path: "/branding/anthropology", label: `Branding Anthropology · ${BRANDING_ANTHROPOLOGY.length} firms`, icon: Palette },
      { note: `Total: ${BRANDING_NATIONAL.length + BRANDING_INTERNATIONAL.length + BRANDING_ANTHROPOLOGY.length} branding entities indexed across all tiers.` },
    ],
  },
  {
    n: "09",
    title: "Orchestration Directory",
    desc: "Conservatories, orchestras, contractors, librarians, and arrangement specialists.",
    icon: Music4,
    items: [
      { path: "/orchestration", label: `Orchestration — ${ORCHESTRATION_RESOURCES.length} resources`, icon: Music4 },
    ],
  },
  {
    n: "10",
    title: "Licensing",
    desc: "Quick alias into the deep licensing topic page.",
    icon: FileText,
    items: [
      { path: "/licensing", label: "Licensing — redirects to /rights-topic/licensing", icon: FileText },
    ],
  },
  {
    n: "11",
    title: "Translation Hub",
    desc: "AI-powered Claude-driven translation across global musical dialects.",
    icon: Languages,
    items: [
      { path: "/translate", label: "Translation Hub — multi-dialect AI translation", icon: Languages },
    ],
  },
  {
    n: "12",
    title: "Commerce — $6.95 Complete Access",
    desc: "Stripe-powered one-time paywall granting lifetime access.",
    icon: Crown,
    items: [
      { path: "/upgrade", label: "Upgrade — $6.95 Complete Access checkout", icon: Crown, protected: true },
      { path: "/payment/success", label: "Payment Success — post-checkout confirmation", icon: CheckCircle2, protected: true },
    ],
  },
  {
    n: "13",
    title: "Legal",
    desc: "All legally required policies linked from the footer of every page.",
    icon: ShieldCheck,
    items: [
      { path: "/legal/terms", label: "Terms & Conditions", icon: Scale },
      { path: "/legal/privacy", label: "Privacy Policy", icon: ShieldCheck },
      { path: "/legal/disclaimer", label: "Disclaimer", icon: AlertTriangle },
      { path: "/legal/refund", label: "Refund Policy", icon: RotateCcw },
    ],
  },
];

// total page count for the hero
const totalPages = SECTIONS.reduce(
  (sum, s) => sum + s.items.filter((i) => i.path || i.externalUrl).length,
  0
);

export default function Sitemap() {
  // Flatten all items into a single alphabetized list (Master Map)
  const allItems = SECTIONS.flatMap((s) => s.items.filter((i) => i.path || i.externalUrl))
    .map((i) => ({
      label: i.label,
      path: i.path || i.externalUrl,
      isExternal: !!i.externalUrl,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div className="min-h-screen bg-[#0A1128] text-white pt-32 pb-24" data-testid="sitemap-page">
      <div className="max-w-6xl mx-auto px-6">
        {/* Hero */}
        <div className="flex items-start gap-5">
          <div className="w-14 h-14 rounded-md border border-[#7DA3D9]/30 bg-[#7DA3D9]/5 flex items-center justify-center flex-shrink-0">
            <Map className="w-6 h-6 text-[#7DA3D9]" />
          </div>
          <div>
            <div className="overline" style={{ color: "#7DA3D9" }}>Reference · Master Map</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-2">
              Site map
            </h1>
            <p className="text-slate-300 mt-5 max-w-2xl leading-relaxed">
              Every public and member page on Over Seas Rights &amp; Royalties — {allItems.length} destinations,
              alphabetized for quick access.
            </p>
          </div>
        </div>

        {/* Alphabetized master list */}
        <ol className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sitemap-alpha-list">
          {allItems.map((it, i) => (
            <li key={i}>
              {it.isExternal ? (
                <a
                  href={it.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start gap-3 p-3 rounded-md border border-white/10 hover:border-[#7DA3D9]/40 hover:bg-white/[0.03] transition-colors"
                >
                  <span className="font-display text-[#D4AF37] text-xs w-8 flex-shrink-0">{String(i + 1).padStart(3, "0")}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-200 truncate group-hover:text-white">{it.label}</div>
                    <div className="text-[10px] text-slate-500 truncate mt-0.5">External</div>
                  </div>
                </a>
              ) : (
                <Link
                  to={it.path}
                  className="group flex items-start gap-3 p-3 rounded-md border border-white/10 hover:border-[#7DA3D9]/40 hover:bg-white/[0.03] transition-colors"
                >
                  <span className="font-display text-[#D4AF37] text-xs w-8 flex-shrink-0">{String(i + 1).padStart(3, "0")}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-200 truncate group-hover:text-white">{it.label}</div>
                    <div className="text-[10px] text-slate-500 truncate mt-0.5 font-mono">{it.path}</div>
                  </div>
                </Link>
              )}
            </li>
          ))}
        </ol>

        <div className="mt-24 pt-10 border-t border-white/10 text-xs text-slate-500 leading-relaxed">
          Master Map auto-generated from the live route table.
        </div>
      </div>
    </div>
  );
}
